import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  PlusCircle,
  Cpu,
  Clock,
  Image as ImageIcon,
  CheckCircle2,
  AlertTriangle,
  Wrench,
  Layers,
  AlertCircle,
  ExternalLink,
  Calendar,
  FileText,
} from "lucide-react";
import { errorAPI } from "../api";
import LogError from "../components/LogError";
import clsx from "clsx";

// Status configuration matching Knowledge, SOPs & Dashboard design system
const statusConfig = {
  Open: {
    label: "Open",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
    icon: AlertTriangle,
  },
  Pending_Maintenance: {
    label: "Pending Maintenance",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
    icon: Clock,
  },
  In_Progress: {
    label: "In Progress",
    badge: "bg-blue-50 text-blue-700 border-blue-200/80",
    dot: "bg-blue-500",
    icon: Wrench,
  },
  Resolved: {
    label: "Resolved",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    dot: "bg-emerald-500",
    icon: CheckCircle2,
  },
};

const formatTimestamp = (dateStr) => {
  if (!dateStr) return { date: "Recent", time: "—" };
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return { date: "Recent", time: "—" };
  return {
    date: d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

/**
 * Renders a single Error Log row matching Knowledge.jsx 12-column layout
 */
const ErrorListItem = ({ err, index, onSelect, onSchedule }) => {
  const statusInfo = statusConfig[err.status] || {
    label: err.status?.replace(/_/g, " ") || "Unknown",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(err)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(err);
          }
        }}
        className="w-full text-left grid grid-cols-12 items-center px-4 py-3.5 text-sm border-b border-slate-100/80 transition-colors hover:bg-blue-50/40 group cursor-pointer last:border-b-0 focus:outline-none focus:bg-blue-50/50"
      >
        {/* Column 1: Machine / Equipment (span 4) */}
        <div className="col-span-4 flex items-center gap-3 min-w-0 pr-4">
          <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 group-hover:bg-blue-600 group-hover:text-white transition-all duration-200 shadow-2xs">
            <Cpu className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors truncate text-sm">
              {err.machineName || "Unknown Machine"}
            </p>
            <p className="text-xs text-slate-400 font-mono uppercase mt-0.5 truncate">
              {err.machineId ? err.machineId.slice(-8) : "N/A"}
            </p>
          </div>
        </div>

        {/* Column 2: Issue Description & Media (span 4) */}
        <div className="col-span-4 min-w-0 pr-4">
          <p className="text-xs sm:text-sm text-slate-700 line-clamp-2 leading-relaxed font-medium">
            {err.description}
          </p>
          {(err.mediaUrls?.length > 0 || err.sopAttempted) && (
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {err.mediaUrls?.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200/60">
                  <ImageIcon className="w-2.5 h-2.5" />
                  <span>{err.mediaUrls.length} Media</span>
                </span>
              )}

              {err.sopAttempted && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200/60">
                  <FileText className="w-2.5 h-2.5" />
                  <span>SOP Attempted</span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Column 3: Timestamp (span 2) */}
        <div className="col-span-2 min-w-0 pr-2">
          {(() => {
            const { date, time } = formatTimestamp(err.createdAt);
            return (
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5 text-xs text-slate-700">
                  <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <span className="font-semibold text-slate-800 truncate">
                    {date}
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 font-mono mt-0.5 pl-5 truncate">
                  {time}
                </p>
              </div>
            );
          })()}
        </div>

        {/* Column 4: Status Badge (span 2, left-aligned) */}
        <div className="col-span-2 flex items-center justify-start">
          <span
            className={clsx(
              "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap",
              statusInfo.badge
            )}
          >
            <span className={clsx("w-1.5 h-1.5 rounded-full", statusInfo.dot)} />
            <span>{statusInfo.label}</span>
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const ErrorLogList = () => {
  const navigate = useNavigate();
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [machineFilter, setMachineFilter] = useState("");
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isMachineOpen, setIsMachineOpen] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const statusRef = useRef(null);
  const machineRef = useRef(null);

  const { data: rawErrors, isLoading } = useQuery({
    queryKey: ["error-logs"],
    queryFn: () => errorAPI.getAll().then((res) => res.data?.data || []),
  });

  const errors = Array.isArray(rawErrors) ? rawErrors : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusRef.current && !statusRef.current.contains(event.target))
        setIsStatusOpen(false);
      if (machineRef.current && !machineRef.current.contains(event.target))
        setIsMachineOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Unique machines for filter dropdown
  const uniqueMachines = useMemo(() => {
    const map = new Map();
    errors.forEach((err) => {
      if (err.machineId && !map.has(err.machineId)) {
        map.set(err.machineId, err.machineName || "Unknown Machine");
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [errors]);

  // Client-side filtering matching search and filter controls
  const filteredErrors = useMemo(() => {
    return errors.filter((err) => {
      // Search match
      if (search.trim()) {
        const q = search.toLowerCase();
        const matchesDesc = err.description?.toLowerCase().includes(q);
        const matchesName = err.machineName?.toLowerCase().includes(q);
        const matchesId = err.machineId?.toLowerCase().includes(q);
        if (!matchesDesc && !matchesName && !matchesId) return false;
      }
      // Status filter
      if (statusFilter && err.status !== statusFilter) {
        return false;
      }
      // Machine filter
      if (machineFilter && err.machineId !== machineFilter) {
        return false;
      }
      return true;
    });
  }, [errors, search, statusFilter, machineFilter]);

  const totalItems = filteredErrors.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;
  const displayedErrors = filteredErrors.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  const handleScheduleTicket = (err) => {
    navigate("/maintenance/schedule", {
      state: {
        bugId: err._id,
        machineId: err.machineId,
        machineName: err.machineName,
        description: err.description,
      },
    });
  };

  const activeMachineObj = uniqueMachines.find((m) => m.id === machineFilter);

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Top Header matching Knowledge, SOPs & Dashboard */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
            Machine Error Logs
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Track equipment diagnostic alerts, operator reports, and schedule corrective maintenance
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setIsReportModalOpen(true)}
            className="btn btn-primary text-xs sm:text-sm py-2 px-3.5 sm:px-4 shadow-md shadow-blue-500/20 whitespace-nowrap inline-flex items-center gap-2 rounded-xl font-semibold"
          >
            <PlusCircle className="w-4 h-4" />
            <span>Report Issue</span>
          </button>
        </div>
      </div>

      {/* Search & Control Filter Bar matching Knowledge.jsx */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Search Input */}
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search error logs by machine or issue..."
            className="w-full pl-9 pr-9 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-xs transition-all"
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          {/* Status Filter Dropdown */}
          <div ref={statusRef} className="relative w-full sm:w-auto">
            <button
              type="button"
              onClick={() => {
                setIsStatusOpen((prev) => !prev);
                setIsMachineOpen(false);
              }}
              className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-xs hover:border-slate-400 rounded-xl"
            >
              <div className="flex items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-slate-500" />
                {statusFilter && statusConfig[statusFilter] ? (
                  <div className="flex items-center gap-1.5">
                    <span
                      className={clsx(
                        "w-2 h-2 rounded-full",
                        statusConfig[statusFilter].dot
                      )}
                    />
                    <span className="font-semibold text-slate-700">
                      {statusConfig[statusFilter].label}
                    </span>
                  </div>
                ) : (
                  <span className="font-semibold text-slate-700">All Status</span>
                )}
              </div>
              <ChevronDown
                className={clsx(
                  "w-3.5 h-3.5 text-slate-500 transition-transform duration-200",
                  isStatusOpen && "rotate-180"
                )}
              />
            </button>

            <AnimatePresence>
              {isStatusOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-1.5 w-full sm:w-52 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-20"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setStatusFilter("");
                      setCurrentPage(1);
                      setIsStatusOpen(false);
                    }}
                    className={clsx(
                      "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                      statusFilter === ""
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <span>All Status</span>
                    {statusFilter === "" && (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    )}
                  </button>

                  {Object.entries(statusConfig).map(([key, config]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setStatusFilter(key);
                        setCurrentPage(1);
                        setIsStatusOpen(false);
                      }}
                      className={clsx(
                        "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                        statusFilter === key
                          ? "bg-blue-50 text-blue-700 font-semibold"
                          : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className={clsx("w-2 h-2 rounded-full", config.dot)} />
                        <span>{config.label}</span>
                      </div>
                      {statusFilter === key && (
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                      )}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Machine Filter Dropdown */}
          <div ref={machineRef} className="relative w-full sm:w-auto">
            <button
              type="button"
              onClick={() => {
                setIsMachineOpen((prev) => !prev);
                setIsStatusOpen(false);
              }}
              className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-xs hover:border-slate-400 rounded-xl"
            >
              <div className="flex items-center gap-2 truncate max-w-[140px] sm:max-w-none">
                <Cpu className="w-3.5 h-3.5 text-slate-500" />
                <span className="font-semibold text-slate-700 truncate">
                  {activeMachineObj ? activeMachineObj.name : "All Equipment"}
                </span>
              </div>
              <ChevronDown
                className={clsx(
                  "w-3.5 h-3.5 text-slate-500 transition-transform duration-200",
                  isMachineOpen && "rotate-180"
                )}
              />
            </button>

            <AnimatePresence>
              {isMachineOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-1.5 w-full sm:w-56 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-20 max-h-64 overflow-y-auto"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setMachineFilter("");
                      setCurrentPage(1);
                      setIsMachineOpen(false);
                    }}
                    className={clsx(
                      "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                      machineFilter === ""
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <span>All Equipment</span>
                    {machineFilter === "" && (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    )}
                  </button>

                  {uniqueMachines.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => {
                        setMachineFilter(m.id);
                        setCurrentPage(1);
                        setIsMachineOpen(false);
                      }}
                      className={clsx(
                        "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium truncate",
                        machineFilter === m.id
                          ? "bg-blue-50 text-blue-700 font-semibold"
                          : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <span className="truncate">{m.name}</span>
                      {machineFilter === m.id && (
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Reset Filters Shortcut */}
          {(search || statusFilter || machineFilter) && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatusFilter("");
                setMachineFilter("");
                setCurrentPage(1);
              }}
              className="text-xs font-semibold text-slate-500 hover:text-rose-600 px-2.5 py-1.5 rounded-xl hover:bg-rose-50 transition-colors"
              title="Reset all filters"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Listing View matching Knowledge.jsx */}
      {isLoading ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="divide-y divide-slate-100">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-12 items-center p-4 border-b border-slate-100 last:border-b-0 animate-pulse"
              >
                {/* Machine / Equipment (4) */}
                <div className="col-span-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-slate-200 flex-shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-4 w-36 bg-slate-200 rounded" />
                    <div className="h-3 w-20 bg-slate-100 rounded" />
                  </div>
                </div>
                {/* Issue Description (4) */}
                <div className="col-span-4 space-y-1.5">
                  <div className="h-4 w-48 bg-slate-200 rounded" />
                  <div className="h-3 w-28 bg-slate-100 rounded" />
                </div>
                {/* Timestamp (2) */}
                <div className="col-span-2 space-y-1">
                  <div className="h-3.5 w-20 bg-slate-200 rounded" />
                  <div className="h-3 w-14 bg-slate-100 rounded" />
                </div>
                {/* Status (2, left-aligned) */}
                <div className="col-span-2 flex items-center">
                  <div className="h-6 w-24 bg-slate-200 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : displayedErrors && displayedErrors.length > 0 ? (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                {/* Table Header (4-4-2-2 = 12 cols) */}
                <div className="grid grid-cols-12 items-center text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50/80 border-b border-slate-200/80 px-4 py-3">
                  <div className="col-span-4">Equipment / Machine</div>
                  <div className="col-span-4">Diagnostic Issue</div>
                  <div className="col-span-2">Timestamp</div>
                  <div className="col-span-2">Status</div>
                </div>

                {/* List Body */}
                <div className="divide-y divide-slate-100/90">
                  <AnimatePresence>
                    {displayedErrors.map((err, index) => (
                      <ErrorListItem
                        key={err._id}
                        err={err}
                        index={index}
                        onSelect={setSelectedError}
                        onSchedule={handleScheduleTicket}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>

          {/* Pagination Controls - only shown after 10 items (totalItems > PAGE_SIZE) */}
          {!isLoading && totalItems > PAGE_SIZE && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200/80">
              <p className="text-xs text-slate-500 font-medium">
                Showing{" "}
                <span className="font-semibold text-slate-700">
                  {(currentPage - 1) * PAGE_SIZE + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-slate-700">
                  {Math.min(currentPage * PAGE_SIZE, totalItems)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-700">{totalItems}</span>{" "}
                error logs
              </p>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  className={clsx(
                    "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                    currentPage <= 1
                      ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                      : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                  )}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Prev</span>
                </button>

                <div className="flex items-center gap-1">
                  {getPageNumbers().map((pageItem, idx) =>
                    pageItem === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageItem}
                        type="button"
                        onClick={() => setCurrentPage(pageItem)}
                        className={clsx(
                          "w-8 h-8 rounded-xl text-xs font-semibold transition-all",
                          currentPage === pageItem
                            ? "bg-blue-600 text-white shadow-sm shadow-blue-500/30 font-bold"
                            : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 shadow-xs"
                        )}
                      >
                        {pageItem}
                      </button>
                    )
                  )}
                </div>

                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  className={clsx(
                    "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                    currentPage >= totalPages
                      ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                      : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                  )}
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Empty State matching Knowledge.jsx */
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-10 sm:p-14 text-center">
          <div
            className={clsx(
              "w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center",
              search || statusFilter || machineFilter
                ? "bg-blue-50 text-blue-500"
                : "bg-emerald-50 text-emerald-500"
            )}
          >
            {search || statusFilter || machineFilter ? (
              <AlertCircle className="w-8 h-8" />
            ) : (
              <CheckCircle2 className="w-8 h-8" />
            )}
          </div>
          <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1.5">
            {search || statusFilter || machineFilter
              ? "No Matching Error Logs Found"
              : "All Systems Operational"}
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 max-w-sm mx-auto mb-5">
            {search || statusFilter || machineFilter
              ? "No machine error reports match your search criteria or active filters."
              : "No machine faults or diagnostic issues have been reported."}
          </p>
          <div className="flex items-center justify-center gap-3">
            {search || statusFilter || machineFilter ? (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("");
                  setMachineFilter("");
                  setCurrentPage(1);
                }}
                className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 rounded-xl"
              >
                Clear Filters
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsReportModalOpen(true)}
                className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 inline-flex items-center gap-2 rounded-xl font-semibold"
              >
                <PlusCircle className="w-4 h-4" />
                <span>Report Issue</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error Details Modal */}
      {typeof document !== "undefined" &&
        selectedError &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
            onClick={() => setSelectedError(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="relative w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl p-5 sm:p-6 space-y-4 my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center shrink-0">
                    <Cpu className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                      {selectedError.machineName || "Machine Unit"}
                    </h3>
                    <p className="text-xs text-slate-400 font-mono">
                      ID: {selectedError.machineId || "N/A"}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedError(null)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Status and Timestamp info */}
              <div className="flex items-center justify-between py-1 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 font-medium">Status:</span>
                  {(() => {
                    const s = statusConfig[selectedError.status] || {
                      label: selectedError.status,
                      badge: "bg-slate-100 text-slate-700",
                      dot: "bg-slate-400",
                    };
                    return (
                      <span
                        className={clsx(
                          "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                          s.badge
                        )}
                      >
                        <span className={clsx("w-1.5 h-1.5 rounded-full", s.dot)} />
                        <span>{s.label}</span>
                      </span>
                    );
                  })()}
                </div>

                <div className="text-slate-400 font-medium flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span>
                    {selectedError.createdAt
                      ? new Date(selectedError.createdAt).toLocaleString()
                      : "Unknown"}
                  </span>
                </div>
              </div>

              {/* Issue Description */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Fault Description
                </label>
                <div className="p-3.5 bg-slate-50/70 border border-slate-200/80 rounded-xl text-xs sm:text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
                  {selectedError.description}
                </div>
              </div>

              {/* Media Attachments Preview */}
              {selectedError.mediaUrls?.length > 0 && (
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Attached Photos ({selectedError.mediaUrls.length})
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {selectedError.mediaUrls.map((url, idx) => (
                      <a
                        key={idx}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="group relative block aspect-video rounded-lg overflow-hidden border border-slate-200 bg-slate-100"
                      >
                        <img
                          src={url}
                          alt={`Attachment ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                        <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                          <ExternalLink className="w-4 h-4" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions in Modal */}
              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setSelectedError(null)}
                  className="btn btn-secondary text-xs sm:text-sm py-2 px-4 rounded-xl"
                >
                  Close
                </button>

                {selectedError.status === "Open" && (
                  <button
                    type="button"
                    onClick={() => {
                      const err = selectedError;
                      setSelectedError(null);
                      handleScheduleTicket(err);
                    }}
                    className="btn btn-primary text-xs sm:text-sm py-2 px-4 rounded-xl shadow-md shadow-blue-500/20 inline-flex items-center gap-1.5 font-semibold"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Create Ticket</span>
                  </button>
                )}
              </div>
            </motion.div>
          </div>,
          document.body
        )}

      {/* Report Issue Popup Modal */}
      <LogError
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
      />
    </div>
  );
};

export default ErrorLogList;
